import { type UseMutationResult } from '@tanstack/react-query';

import { ApiError } from '../../../lib/api/errors.js';
import { parseOrThrow } from '../../../lib/api/contract.js';
import { useApiMutation } from '../../../lib/api/hooks.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { tenantEndpoints } from './endpoints.js';
import { tenantDetail, type TenantDetail, type TenantFormInput } from './schemas.js';

/** 404. Named because the linter forbids a bare numeric literal here. */
const HTTP_NOT_FOUND = 404;

/**
 * Creates a tenant.
 *
 * The form collects empty strings for optional fields; the API prefers those
 * omitted, so the mutation normalises before the request.
 */
function normalise(input: TenantFormInput): Record<string, unknown> {
  // `admins` and `attachmentCredentials` are destructured out and deliberately
  // unused: `admins` is sent under a different key by each caller, and
  // `attachmentCredentials` belongs to the attachment service.
  const {
    countryId,
    stateId,
    cityId,
    email,
    attachmentCredentials: _attachmentCredentials,
    admins: _admins,
    ...rest
  } = input;
  return {
    ...rest,
    ...(countryId === '' ? {} : { countryId }),
    ...(stateId === '' ? {} : { stateId }),
    ...(cityId === '' ? {} : { cityId }),
    ...(email === '' ? {} : { email }),
  };
}

/**
 * The administrators a form is submitting, with blanks dropped.
 *
 * Password is optional to the API, and an empty string is not the same as
 * absent -- a blank password would be rejected against policy rather than
 * understood as "no password chosen". The name is validated as non-empty by
 * the form schema, so it always travels.
 */
function normaliseAdmins(
  admins: TenantFormInput['admins'],
): { readonly email: string; readonly name: string; readonly password?: string }[] {
  return (admins ?? [])
    .filter((admin) => admin.email !== '')
    .map((admin) => ({
      email: admin.email,
      name: admin.name,
      ...(admin.password === undefined || admin.password === ''
        ? {}
        : { password: admin.password }),
    }));
}

export function useCreateTenant(): UseMutationResult<TenantDetail, unknown, TenantFormInput> {
  return useApiMutation<TenantFormInput, TenantDetail>({
    mutationFn: async (api, input) => {
      const { attachmentCredentials } = input;
      const payload = await api.post<unknown>(
        tenantEndpoints.create,
        { ...normalise(input), admins: normaliseAdmins(input.admins) },
        crypto.randomUUID(),
      );
      const tenant = parseOrThrow(tenantDetail, payload, tenantEndpoints.create);
      if (attachmentCredentials !== undefined) {
        await api.post<unknown>(
          tenantEndpoints.attachmentCredentials(tenant.id),
          attachmentCredentials,
        );
      }
      return tenant;
    },
    invalidate: [queryKeys.tenants.all],
  });
}

export function useUpdateTenant(): UseMutationResult<
  TenantDetail,
  unknown,
  { readonly tenantId: string; readonly version: number; readonly values: TenantFormInput }
> {
  return useApiMutation<
    { readonly tenantId: string; readonly version: number; readonly values: TenantFormInput },
    TenantDetail
  >({
    mutationFn: async (api, { tenantId, version, values }) => {
      const { attachmentCredentials } = values;
      const added = normaliseAdmins(values.admins);
      const payload = await api.put<unknown>(tenantEndpoints.update(tenantId), {
        ...normalise(values),
        // `newAdmins`, and omitted when empty: the route is additive and never
        // removes, so an empty array would be a no-op that still reads like a
        // list submission. Sending nothing says nothing is being added.
        ...(added.length === 0 ? {} : { newAdmins: added }),
        // The version the editor loaded, not the one on screen now. This is
        // the whole lock: without it the server cannot tell a fresh edit from
        // one that started before someone else saved.
        version,
      });
      const tenant = parseOrThrow(tenantDetail, payload, tenantEndpoints.update(tenantId));
      if (attachmentCredentials !== undefined) {
        try {
          await api.put<unknown>(
            tenantEndpoints.attachmentCredentials(tenantId),
            attachmentCredentials,
          );
        } catch (error) {
          if (error instanceof ApiError && error.status === HTTP_NOT_FOUND) {
            await api.post<unknown>(
              tenantEndpoints.attachmentCredentials(tenantId),
              attachmentCredentials,
            );
          } else {
            throw error;
          }
        }
      }
      return tenant;
    },
    invalidate: [queryKeys.tenants.all],
  });
}

/**
 * Replaces a tenant's modules, without touching the rest of the record.
 *
 * Separate from the update because it is a separate authority: assigning a
 * module is a commercial decision guarded by its own permission, not part of
 * editing a tenant's address.
 */
export function useAssignModules(): UseMutationResult<
  TenantDetail,
  unknown,
  { readonly tenantId: string; readonly enabledModules: readonly string[] }
> {
  return useApiMutation<
    { readonly tenantId: string; readonly enabledModules: readonly string[] },
    TenantDetail
  >({
    mutationFn: async (api, { tenantId, enabledModules }) => {
      const path = tenantEndpoints.assignModules(tenantId);
      const payload = await api.put<unknown>(path, { enabledModules: [...enabledModules] });
      return parseOrThrow(tenantDetail, payload, path);
    },
    invalidate: [queryKeys.tenants.all],
  });
}

export function useSoftDeleteTenant(): UseMutationResult<unknown, unknown, string> {
  return useApiMutation<string, unknown>({
    mutationFn: async (api, tenantId) => {
      await api.delete<unknown>(tenantEndpoints.delete(tenantId));
    },
    invalidate: [queryKeys.tenants.all],
  });
}

/**
 * Activates or deactivates one of a tenant's administrators.
 *
 * Deactivating stops the next sign-in and the next refresh. It does not end
 * a session already running -- permissions are baked into the access token
 * at login -- so the wording in the UI says 'cannot sign in' rather than
 * implying they are thrown out.
 */
export function useSetAdminStatus(
  tenantId: string,
): UseMutationResult<unknown, unknown, { readonly userId: string; readonly status: string }> {
  return useApiMutation<{ readonly userId: string; readonly status: string }, unknown>({
    mutationFn: async (api, { userId, status }) =>
      await api.patch<unknown>(tenantEndpoints.adminStatus(tenantId, userId), { status }),
    invalidate: [queryKeys.tenants.all],
  });
}
